import { useEffect } from "react";
import { Pencil, Trash2, X } from "lucide-react";
import {
  featureRequestStatusLabel,
  featureRequestStatusMeta,
} from "../constants/featureRequests";
import type { OnboardingRequest, OnboardingRequestType } from "../types";
import {
  displayOnboardingValue,
  ONBOARDING_STATUSES,
} from "../utils/onboardingRequests";

export function OnboardingStatusBadge({ status }: { status: string }) {
  const meta = featureRequestStatusMeta(status);
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
        meta?.badgeClasses ??
        "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
      }`}
    >
      {meta?.label ?? status}
    </span>
  );
}

interface OnboardingRequestDetailModalProps {
  request: OnboardingRequest;
  type: OnboardingRequestType | undefined;
  isAdmin: boolean;
  statusUpdating: boolean;
  onStatusChange: (status: string) => void;
  onEdit?: (() => void) | undefined;
  onDelete?: (() => void) | undefined;
  deleting?: boolean;
  onClose: () => void;
}

export default function OnboardingRequestDetailModal({
  request,
  type,
  isAdmin,
  statusUpdating,
  onStatusChange,
  onEdit,
  onDelete,
  deleting = false,
  onClose,
}: OnboardingRequestDetailModalProps) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const groups = type?.groups ?? [
    {
      title: "Details",
      fields: Object.keys(request.fields).map((key) => ({
        key,
        label: key,
        kind: "text" as const,
        required: false,
      })),
    },
  ];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-request-title"
        className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2
              id="onboarding-request-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              {request.title}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {isAdmin ? (
                <select
                  aria-label="Status"
                  value={request.status}
                  onChange={(e) => onStatusChange(e.target.value)}
                  disabled={statusUpdating}
                  className="input w-auto py-1 text-sm"
                >
                  {ONBOARDING_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {featureRequestStatusLabel(status)}
                    </option>
                  ))}
                </select>
              ) : (
                <OnboardingStatusBadge status={request.status} />
              )}
              {type && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {type.label}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {request.requesterName || request.requesterEmail} ·{" "}
              {new Date(request.createdAt).toLocaleString()}
            </p>
            {request.statusChangedAt && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Status set to {featureRequestStatusLabel(request.status)} on{" "}
                {new Date(request.statusChangedAt).toLocaleString()} by{" "}
                {request.statusChangedByName || request.statusChangedBy}
              </p>
            )}
            {request.editedAt && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Edited on {new Date(request.editedAt).toLocaleString()} by{" "}
                {request.editedByName || request.editedBy}
              </p>
            )}
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                className="btn btn-secondary inline-flex items-center gap-1"
              >
                <Pencil className="h-4 w-4" />
                Edit
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                onClick={onDelete}
                disabled={deleting}
                className="btn btn-secondary inline-flex items-center gap-1 text-red-600 dark:text-red-400"
              >
                <Trash2 className="h-4 w-4" />
                {deleting ? "Deleting..." : "Delete"}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="btn-icon"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 space-y-5">
          {groups.map((group) => (
            <section key={group.title}>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {group.title}
              </h3>
              <dl className="mt-2 divide-y divide-gray-100 dark:divide-gray-700">
                {group.fields.map((field) => (
                  <div
                    key={field.key}
                    className="grid grid-cols-3 gap-4 py-2 text-sm"
                  >
                    <dt className="text-gray-500 dark:text-gray-400">
                      {field.label}
                    </dt>
                    <dd className="col-span-2 break-words text-gray-900 dark:text-gray-100">
                      {displayOnboardingValue(request.fields[field.key])}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
